/**
 * Serbian and English locale utilities for calendar
 */

export type Locale = 'sr' | 'en'

// ==================== Day Names ====================

const DAY_NAMES = {
  sr: ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'],
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
}

const DAY_NAMES_SHORT = {
  sr: ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
}

const DAY_NAMES_NARROW = {
  sr: ['N', 'P', 'U', 'S', 'Č', 'P', 'S'],
  en: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
}

// ==================== Month Names ====================

const MONTH_NAMES = {
  sr: [
    'Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun',
    'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'
  ],
  en: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ],
}

const MONTH_NAMES_SHORT = {
  sr: ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Avg', 'Sep', 'Okt', 'Nov', 'Dec'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
}

// ==================== UI Labels ====================

const UI_LABELS = {
  sr: {
    today: 'Danas',
    day: 'Dan',
    week: 'Nedelja',
    month: 'Mesec',
    allDoctors: 'Svi lekari',
    noEvents: 'Nema zakazanih termina',
    loading: 'Učitavanje...',
    createEvent: 'Zakaži termin',
    minutes: 'min',
    hours: 'h',
  },
  en: {
    today: 'Today',
    day: 'Day',
    week: 'Week',
    month: 'Month',
    allDoctors: 'All Doctors',
    noEvents: 'No scheduled appointments',
    loading: 'Loading...',
    createEvent: 'Schedule appointment',
    minutes: 'min',
    hours: 'h',
  },
}

// ==================== Getters ====================

export function getDayName(dayIndex: number, locale: Locale): string {
  return DAY_NAMES[locale][dayIndex]
}

export function getDayNameShort(dayIndex: number, locale: Locale): string {
  return DAY_NAMES_SHORT[locale][dayIndex]
}

export function getDayNameNarrow(dayIndex: number, locale: Locale): string {
  return DAY_NAMES_NARROW[locale][dayIndex]
}

export function getMonthName(monthIndex: number, locale: Locale): string {
  return MONTH_NAMES[locale][monthIndex]
}

export function getMonthNameShort(monthIndex: number, locale: Locale): string {
  return MONTH_NAMES_SHORT[locale][monthIndex]
}

export function getUILabel(key: keyof typeof UI_LABELS['en'], locale: Locale): string {
  return UI_LABELS[locale][key]
}

// ==================== Formatters ====================

/**
 * Format date as "15. januar 2026" or "January 15, 2026"
 */
export function formatDateLong(date: Date, locale: Locale): string {
  const day = date.getDate()
  const month = getMonthName(date.getMonth(), locale)
  const year = date.getFullYear()

  if (locale === 'sr') {
    return `${day}. ${month.toLowerCase()} ${year}`
  }
  return `${month} ${day}, ${year}`
}

/**
 * Format date as "15. jan" or "Jan 15"
 */
export function formatDateShort(date: Date, locale: Locale): string {
  const day = date.getDate()
  const month = getMonthNameShort(date.getMonth(), locale)

  if (locale === 'sr') {
    return `${day}. ${month.toLowerCase()}`
  }
  return `${month} ${day}`
}

/**
 * Format date as "Ponedeljak, 15. januar" or "Monday, January 15"
 */
export function formatDateWithDay(date: Date, locale: Locale): string {
  const dayName = getDayName(date.getDay(), locale)
  const day = date.getDate()
  const month = getMonthName(date.getMonth(), locale)

  if (locale === 'sr') {
    return `${dayName}, ${day}. ${month.toLowerCase()}`
  }
  return `${dayName}, ${month} ${day}`
}

/**
 * Format time as "09:30"
 */
export function formatTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/**
 * Format time from hours and minutes
 */
export function formatTimeFromParts(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/**
 * Format duration as "30 min" or "1h 30min"
 */
export function formatDuration(minutes: number, locale: Locale): string {
  const labels = UI_LABELS[locale]

  if (minutes < 60) {
    return `${minutes} ${labels.minutes}`
  }

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  if (remainingMinutes === 0) {
    return `${hours}${labels.hours}`
  }

  return `${hours}${labels.hours} ${remainingMinutes}${labels.minutes}`
}

/**
 * Format month header as "Januar 2026" or "January 2026"
 */
export function formatMonthYear(date: Date, locale: Locale): string {
  const month = getMonthName(date.getMonth(), locale)
  const year = date.getFullYear()
  return `${month} ${year}`
}

/**
 * Format week range as "13 - 19. januar 2026"
 */
export function formatWeekRange(weekStart: Date, locale: Locale): string {
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekEnd.getDate() + 6)

  const startDay = weekStart.getDate()
  const endDay = weekEnd.getDate()

  // Same month
  if (weekStart.getMonth() === weekEnd.getMonth()) {
    const month = getMonthName(weekStart.getMonth(), locale)
    const year = weekStart.getFullYear()

    if (locale === 'sr') {
      return `${startDay} - ${endDay}. ${month.toLowerCase()} ${year}`
    }
    return `${month} ${startDay} - ${endDay}, ${year}`
  }

  // Different months
  const startMonth = getMonthNameShort(weekStart.getMonth(), locale)
  const endMonth = getMonthNameShort(weekEnd.getMonth(), locale)

  if (locale === 'sr') {
    return `${startDay}. ${startMonth.toLowerCase()} - ${endDay}. ${endMonth.toLowerCase()}`
  }
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}`
}

/**
 * Format date as "15.01.2026" (numeric format)
 * Alias for formatDateShort for backward compatibility
 */
export function formatDate(date: Date, locale: Locale = 'sr'): string {
  return formatDateShort(date, locale)
}
